import { ArrowNarrowRight, FilterLines } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ButtonCmp from 'src/components/ButtonCmp';
import CustomPagination from 'src/components/CustomPagination';
import NotFoundUI from 'src/components/NotFoundUI';
import SelectBox from 'src/components/SelectBox/SelectBox';
import TabButton from 'src/components/TabButton';
import { PATH } from 'src/constants/path';
import { RootState } from 'src/redux/store';
import { acknowledgeReviewItem, listCustomerActivities, listReviewItems, markNotificationSeen } from 'src/services/DashboardService';
import { mapActivitiesToDisplayItems } from 'src/utils/activityFeed.helpers';
import WalToast from 'src/utils/WalToast';

import { getReviewItemDeepLink, getReviewItemDisplayMap, getReviewItemNavState, IReviewItem } from './reviewItems.helpers';

// How many recent activities to pull for the dashboard preview card (no pagination here — full history lives on a dedicated page).
const ACTIVITY_FETCH_LIMIT = 10;

// How many review items to show on the dashboard preview card — the API paginates, this just requests page 1.
const REVIEW_ITEMS_FETCH_LIMIT = 10;

// TODO(product): once confirmed, restrict to a specific allowlist of customer-facing activityName
// values here (e.g. ['quote_created_by_customer', 'order_status_updated_by_user', ...]). The API
// already strips system/internal-only noise server-side — leaving this empty shows everything it returns.
const ACTIVITY_TYPE_ALLOWLIST: string[] = [];

// Filter options for the Actions Items tab's filter panel (matches the 5 review-item rule types).
const REVIEW_ITEM_TYPE_FILTER_OPTIONS: { value: IReviewItem['itemType']; label: string }[] = [
    { value: 'QUOTE_REVIEW', label: 'Quote Review' },
    { value: 'BOL_REVIEW', label: 'Bill of Lading' },
    { value: 'DELIVERY_TIMES', label: 'Delivery Times' },
    { value: 'POD_REVIEW', label: 'Proof of Delivery' },
    { value: 'CUSTOMS_DOCUMENT', label: 'Customs Documents' },
];

// Filter options for the Latest Activity tab's filter panel (module type = quote vs order).
const ACTIVITY_ENTITY_TYPE_FILTER_OPTIONS = [
    { value: 'quote', label: 'Quote' },
    { value: 'order', label: 'Order' },
];

type TabValue = 'actions' | 'activity';

// Compact tab-badge counts — 1000 -> "1k", 1500000 -> "1.5m", 2000000000 -> "2b".
const formatCompactCount = (value: number): string => {
    const units: { threshold: number; suffix: string }[] = [
        { threshold: 1_000_000_000, suffix: 'b' },
        { threshold: 1_000_000, suffix: 'm' },
        { threshold: 1_000, suffix: 'k' },
    ];
    const unit = units.find((u) => value >= u.threshold);

    if (!unit) return `${value}`;

    const scaled = Math.round((value / unit.threshold) * 10) / 10;

    return `${Number.isInteger(scaled) ? scaled.toFixed(0) : scaled.toFixed(1)}${unit.suffix}`;
};

// Replaces the text "→" in lane subtitles (e.g. "Buena Park, CA → Los Angeles, CA") with the
// untitled-ui arrow icon — the raw glyph renders from a fallback font and doesn't match the design.
const SubtitleWithLaneArrow = ({ text }: { text: string }) => {
    const parts = text.split(/\s*→\s*/);

    if (parts.length === 1) return <>{text}</>;

    return (
        <>
            {parts.map((part, index) => (
                <React.Fragment key={index}>
                    {index > 0 && <ArrowNarrowRight className="inline-block w-3.5 h-3.5 mx-1 align-[-3px]" />}
                    {part}
                </React.Fragment>
            ))}
        </>
    );
};

// Renders an activity's description, turning the trailing reference code (e.g. "SQ-TC8C-1425")
// into a link to that specific quote/order — this feed spans many entities, so being able to
// jump straight to the one a row is about matters more here than in the single-entity dropdowns.
const ActivityMessage = ({ item }: { item: ReturnType<typeof mapActivitiesToDisplayItems>[number] }) => {
    const navigate = useNavigate();

    if (!item.displayMessage) return null;

    let referenceIdx = item.referenceId && item.entityId ? item.displayMessage.lastIndexOf(item.referenceId) : -1;

    if (referenceIdx === -1) {
        return <p className="text-xs text-grayLight600 mt-0.5 leading-[18px]">{item.displayMessage}</p>;
    }

    // Pull a leading "#" into the linked text so "#WAL-TC3B-855" underlines as one unit.
    let referenceText = item.referenceId!;

    if (referenceIdx > 0 && item.displayMessage[referenceIdx - 1] === '#') {
        referenceIdx -= 1;
        referenceText = `#${referenceText}`;
    }

    const before = item.displayMessage.slice(0, referenceIdx);
    const after = item.displayMessage.slice(referenceIdx + referenceText.length);
    const targetPath = item.entityType === 'order' ? `${PATH.ORDER_VIEW}/${item.entityId}` : `${PATH.MY_QUOTE}/${item.entityId}`;

    return (
        <p className="text-xs text-grayLight600 mt-0.5 leading-[18px]">
            {before}
            <span
                role="link"
                tabIndex={0}
                className="text-primary700 font-medium cursor-pointer"
                onClick={(e) => {
                    e.stopPropagation();
                    navigate(targetPath);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') navigate(targetPath);
                }}
            >
                {referenceText}
            </span>
            {after}
        </p>
    );
};

const ActivityFeed = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    // WAL staff ("view as customer") sessions have no contacts_customers row, so the portal
    // backend refuses every per-login write (acknowledge / mark-seen) by design — nothing may be
    // recorded against a staff login. Checked here so those calls are never fired at all, instead
    // of firing them and surfacing the resulting 403 to the viewer as "Something went wrong".
    const isWalUserSession = useSelector((state: RootState) => !!state.auth.currentUser?.isWalUserSession);
    const [activeTab, setActiveTab] = useState<TabValue>('actions');
    const [activities, setActivities] = useState<any[]>([]);
    const [isActivitiesLoading, setIsActivitiesLoading] = useState(true);
    const [reviewItems, setReviewItems] = useState<IReviewItem[]>([]);
    const [isReviewItemsLoading, setIsReviewItemsLoading] = useState(true);
    const [reviewItemsPage, setReviewItemsPage] = useState(1);
    const [reviewItemsTotal, setReviewItemsTotal] = useState(0);
    // Badge counts — distinct from the *Total state above, which is the full list size used for
    // pagination. Unseen count only drops when this user opens an item; the list itself doesn't
    // shrink until the underlying task is actually resolved (or, for Latest Activity, never).
    const [reviewItemsUnseenCount, setReviewItemsUnseenCount] = useState(0);
    const [activitiesPage, setActivitiesPage] = useState(1);
    const [activitiesTotal, setActivitiesTotal] = useState(0);
    const [activitiesUnseenCount, setActivitiesUnseenCount] = useState(0);

    // Filter panel — one shared button/panel, content switches based on the active tab.
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const filterPanelRef = useRef<HTMLDivElement>(null);
    const [selectedItemTypes, setSelectedItemTypes] = useState<string[]>([]);
    const [stagedItemTypes, setStagedItemTypes] = useState<string[]>([]);
    const [selectedEntityTypes, setSelectedEntityTypes] = useState<string[]>([]);
    const [stagedEntityTypes, setStagedEntityTypes] = useState<string[]>([]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (filterPanelRef.current && !event.composedPath().includes(filterPanelRef.current)) {
                setIsFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleOpenFilter = () => {
        setStagedItemTypes(selectedItemTypes);
        setStagedEntityTypes(selectedEntityTypes);
        setIsFilterOpen((prev) => !prev);
    };

    const handleResetFilters = () => {
        setStagedItemTypes([]);
        setStagedEntityTypes([]);
    };

    const handleApplyFilters = () => {
        if (activeTab === 'actions') {
            setSelectedItemTypes(stagedItemTypes);
            setReviewItemsPage(1);
        } else {
            setSelectedEntityTypes(stagedEntityTypes);
            setActivitiesPage(1);
        }
        setIsFilterOpen(false);
    };

    const appliedFilterCount = activeTab === 'actions' ? selectedItemTypes.length : selectedEntityTypes.length;

    useEffect(() => {
        let cancelled = false;
        setIsReviewItemsLoading(true);

        listReviewItems({ page: reviewItemsPage, limit: REVIEW_ITEMS_FETCH_LIMIT, itemTypes: selectedItemTypes.length ? selectedItemTypes.join(',') : undefined })
            .then((response: any) => {
                if (cancelled) return;
                // Trust the backend's order as-is — it already sorts unseen-first, then by
                // recency within each group. Re-sorting here by recency alone would interleave
                // seen/unseen items on the page and undo that grouping.
                setReviewItems(response?.data?.reviewItems ?? []);
                setReviewItemsTotal(response?.data?.pagination?.totalItems ?? 0);
                setReviewItemsUnseenCount(response?.data?.unseenCount ?? 0);
            })
            .catch((error: any) => {
                if (cancelled) return;
                // Fail quietly to the empty state — this tab shouldn't block the rest of the dashboard.
                console.error('ActivityFeed: failed to fetch review items', { error: error.message });
                setReviewItems([]);
                setReviewItemsTotal(0);
                setReviewItemsUnseenCount(0);
            })
            .finally(() => {
                if (!cancelled) setIsReviewItemsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [reviewItemsPage, selectedItemTypes]);

    const handleReviewItemsPagination = (pageNumber: number) => setReviewItemsPage(pageNumber);

    const reviewItemDisplayMap = useMemo(() => getReviewItemDisplayMap(t), [t]);

    // Navigation happens FIRST, synchronously — the tracking/acknowledge call runs in the
    // background afterward. Awaiting it before navigate() was the bug: the click would sit
    // through a full network round-trip before the page even started redirecting, and any
    // state update from the response would land after navigate() unmounts this component
    // anyway, so it was never visible even once the call did resolve.
    const handleReviewItemCtaClick = (item: IReviewItem) => {
        const display = reviewItemDisplayMap[item.itemType];

        // Nav state asks the destination page to open the modal this item is about (BOL preview,
        // customs upload) instead of leaving the customer to find it — see getReviewItemNavState.
        navigate(getReviewItemDeepLink(item), { state: getReviewItemNavState(item) });

        // A WAL staff session must leave no per-login trace: both calls below are 403'd / no-op'd
        // server-side for it, so skip them rather than fire-and-toast. The item stays in the list,
        // which is correct — nothing was acknowledged or seen on the customer's behalf.
        if (isWalUserSession) return;

        if (display.requiresAcknowledgement) {
            // BOL/Delivery Times/POD — resolving IS the only "seen" signal for these; being in
            // the list at all already counts toward the badge, so resolving drops both counts.
            acknowledgeReviewItem({ itemType: item.itemType, entityType: item.entityType, entityId: item.entityId })
                .then(() => {
                    setReviewItems((prev) => prev.filter((reviewItem) => reviewItem.id !== item.id));
                    setReviewItemsTotal((prev) => Math.max(0, prev - 1));
                    setReviewItemsUnseenCount((prev) => Math.max(0, prev - 1));
                })
                .catch((error: any) => {
                    console.error('ActivityFeed: failed to acknowledge review item', { error: error.message });
                    WalToast.error(t('Common.Something_went_wrong'));
                });
        } else {
            // Quote Review/Customs Document — no resolution signal exists (they clear on their
            // own once the underlying quote/order state changes), so opening only marks "seen";
            // the item stays in the list either way.
            markNotificationSeen({ itemType: item.itemType as 'QUOTE_REVIEW' | 'CUSTOMS_DOCUMENT', entityId: item.entityId })
                .then((response: any) => {
                    if (!response?.data?.alreadySeen) {
                        setReviewItemsUnseenCount((prev) => Math.max(0, prev - 1));
                    }
                })
                .catch((error: any) => {
                    console.error('ActivityFeed: failed to mark review item as seen', { error: error.message });
                });
        }
    };

    const handleActivityRowClick = (item: ReturnType<typeof mapActivitiesToDisplayItems>[number]) => {
        if (item.entityId) {
            navigate(item.entityType === 'order' ? `${PATH.ORDER_VIEW}/${item.entityId}` : `${PATH.MY_QUOTE}/${item.entityId}`);
        }

        // Seen-state is per portal login and a WAL staff session has none — the backend answers
        // this as a no-op for it, so don't make the round trip.
        if (isWalUserSession) return;

        markNotificationSeen({ itemType: 'ACTIVITY', entityId: item.id as number })
            .then((response: any) => {
                if (!response?.data?.alreadySeen) {
                    setActivitiesUnseenCount((prev) => Math.max(0, prev - 1));
                }
            })
            .catch((error: any) => {
                console.error('ActivityFeed: failed to mark activity as seen', { error: error.message });
            });
    };

    useEffect(() => {
        let cancelled = false;
        setIsActivitiesLoading(true);

        listCustomerActivities({ page: activitiesPage, limit: ACTIVITY_FETCH_LIMIT, entityTypes: selectedEntityTypes.length ? selectedEntityTypes.join(',') : undefined })
            .then((response: any) => {
                if (cancelled) return;
                setActivities(response?.data?.activities ?? []);
                setActivitiesTotal(response?.data?.pagination?.totalItems ?? 0);
                setActivitiesUnseenCount(response?.data?.unseenCount ?? 0);
            })
            .catch((error: any) => {
                if (cancelled) return;
                console.error('ActivityFeed: failed to fetch customer activities', { error: error.message });
                setActivities([]);
                setActivitiesTotal(0);
                setActivitiesUnseenCount(0);
                WalToast.error(t('Common.Something_went_wrong'));
            })
            .finally(() => {
                if (!cancelled) setIsActivitiesLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [t, selectedEntityTypes, activitiesPage]);

    const handleActivitiesPagination = (pageNumber: number) => setActivitiesPage(pageNumber);

    const filteredActivities = useMemo(
        () => (ACTIVITY_TYPE_ALLOWLIST.length > 0 ? activities.filter((activity) => ACTIVITY_TYPE_ALLOWLIST.includes(activity.activityName)) : activities),
        [activities]
    );

    const activityDisplayItems = useMemo(() => mapActivitiesToDisplayItems(filteredActivities), [filteredActivities]);

    const tabsData = useMemo(
        () => [
            {
                name: t('Dashboard.ActivityFeed.Actions_Items'),
                value: 'actions',
                // TabButton renders `count` as-is and only uses it numerically for its own
                // `count > 0` visibility check — passing a compact string there would make that
                // check evaluate to NaN, so visibility is driven by isAlwaysCountShow instead.
                count: formatCompactCount(reviewItemsUnseenCount),
                isAlwaysCountShow: reviewItemsUnseenCount > 0,
            },
            {
                name: t('Dashboard.ActivityFeed.Latest_Activity'),
                value: 'activity',
                count: formatCompactCount(activitiesUnseenCount),
                isAlwaysCountShow: activitiesUnseenCount > 0,
            },
        ],
        [t, activitiesUnseenCount, reviewItemsUnseenCount]
    );

    const handleTabClick = (e: React.MouseEvent<HTMLDivElement>) => {
        setActiveTab(e.currentTarget.dataset.value as TabValue);
    };

    return (
        <div className="flex flex-col space-y-5 mb-3 w-full">
            <div className="flex items-center justify-between">
                <TabButton
                    tabArray={tabsData}
                    tabParentClassName="!w-full"
                    isActive={activeTab}
                    parentClassName="tab-border-bottom-parent w-full !shadow-none !border-b !border-borderPrimary"
                    activeClassName="tab-border-bottom-active"
                    className="tab-border-bottom min-w-[240px] max-w-[240px] flex-1"
                    tabBadgeClassName="tab-border-bottom-badge"
                    handleOnClick={handleTabClick}
                />
                <div className="relative flex-none ml-2" ref={filterPanelRef}>
                    <ButtonCmp size="square-md" className="btn_secondary_black relative" onClick={handleOpenFilter}>
                        <FilterLines className="w-4 h-4" />
                        {appliedFilterCount > 0 && <span className="absolute top-[3px] right-[3px] w-1.5 h-1.5 rounded-full bg-fgErrorPrimary flex items-center justify-center leading-none"></span>}
                    </ButtonCmp>

                    {isFilterOpen && (
                        <div className="absolute right-0 top-full mt-1 z-[99] w-[280px] bg-white rounded-lg shadow-md border border-borderPrimary flex flex-col">
                            <div className="p-3 flex flex-col gap-3">
                                {activeTab === 'actions' ? (
                                    <div className="flex flex-col">
                                        <SelectBox
                                            name="itemTypeFilter"
                                            id="itemTypeFilter"
                                            labelClassName="form_label mb-1.5 block"
                                            label={t('Dashboard.ActivityFeed.Filter_Item_Type')}
                                            placeholder={t('Dashboard.ActivityFeed.Filter_Item_Type_Placeholder')}
                                            options={REVIEW_ITEM_TYPE_FILTER_OPTIONS}
                                            value={REVIEW_ITEM_TYPE_FILTER_OPTIONS.filter((option) => stagedItemTypes.includes(option.value))}
                                            onChangeFunc={(selected: any) => setStagedItemTypes((selected ?? []).map((option: any) => option.value))}
                                            isMultipleSelection
                                            isSearchable={false}
                                            isClearable
                                        />
                                    </div>
                                ) : (
                                    <div className="flex flex-col">
                                        <SelectBox
                                            name="entityTypeFilter"
                                            id="entityTypeFilter"
                                            labelClassName="form_label mb-1.5 block"
                                            label={t('Dashboard.ActivityFeed.Filter_Module_Type')}
                                            placeholder={t('Dashboard.ActivityFeed.Filter_Module_Type_Placeholder')}
                                            options={ACTIVITY_ENTITY_TYPE_FILTER_OPTIONS}
                                            value={ACTIVITY_ENTITY_TYPE_FILTER_OPTIONS.filter((option) => stagedEntityTypes.includes(option.value))}
                                            onChangeFunc={(selected: any) => setStagedEntityTypes((selected ?? []).map((option: any) => option.value))}
                                            isMultipleSelection
                                            isSearchable={false}
                                            isClearable
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-3 pt-3 pb-3 border-t bg-gray50 rounded-b-lg border-borderPrimary px-3">
                                <ButtonCmp className="btn_secondary_black flex-1" onClick={handleResetFilters}>
                                    {t('Common.Reset_All')}
                                </ButtonCmp>
                                <ButtonCmp className="btn_primary flex-1" onClick={handleApplyFilters}>
                                    {t('Common.Apply_Filters')}
                                </ButtonCmp>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="w-full bg-white rounded-xl shadow border border-utilityGray200 overflow-hidden">
                {activeTab === 'actions' ? (
                    <>
                        {isReviewItemsLoading ? (
                            <ul className="w-full">
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((skeletonRow) => (
                                    <li
                                        key={skeletonRow}
                                        className="flex items-start justify-between gap-x-4 px-3 py-3 relative after:absolute after:left-3 after:right-3 after:bottom-0 after:h-px after:bg-utilityGray200 last:after:hidden"
                                    >
                                        <div className="flex items-start gap-x-2 flex-1 min-w-0">
                                            <span className="mt-[5px] h-1.5 w-1.5 rounded-full flex-none custom-loading" />
                                            <div className="flex flex-col min-w-0">
                                                <h6 className="text-xs font-semibold leading-[18px] custom-loading">Review Your Quote #SQ-TC8C-0000</h6>
                                                <p className="text-xs leading-[18px] mt-0.5 custom-loading">Your rate for Buena Park, CA to Los Angeles, CA is locked until Jul 9, 8:00 PM.</p>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        ) : reviewItems.length === 0 ? (
                            <NotFoundUI
                                title={t('Dashboard.ActivityFeed.All_Clear_Title')}
                                desc={t('Dashboard.ActivityFeed.All_Clear_Desc')}
                                containerClassName="!h-auto !my-0"
                                iconClassName="!hidden"
                                isFixedHeight={true}
                                isEmptyBackground
                                iconSecondaryClassName="!hidden"
                            />
                        ) : (
                            <ul className="w-full">
                                {reviewItems.map((item) => {
                                    const display = reviewItemDisplayMap[item.itemType];

                                    return (
                                        <li
                                            key={item.id}
                                            className="flex items-start justify-between gap-x-4 px-3 py-3 relative after:absolute after:left-3 after:right-3 after:bottom-0 after:h-px after:bg-utilityGray200 last:after:hidden cursor-pointer hover:bg-utilityGray100 transition-colors"
                                            onClick={() => handleReviewItemCtaClick(item)}
                                        >
                                            <div className="flex items-start gap-x-2 flex-1 min-w-0">
                                                <span className={`mt-[5px] h-1.5 w-1.5 rounded-full flex-none ${display.dotColor}`} />
                                                <div className="flex flex-col min-w-0">
                                                    <h6 className="text-xs font-semibold text-grayLight900 leading-[18px] truncate">{display.getTitle(item)}</h6>
                                                    <p className="text-xs text-grayLight600 leading-[18px] font-normal mt-0.5">
                                                        <SubtitleWithLaneArrow text={display.getSubtitle(item)} />
                                                    </p>
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        {reviewItemsTotal > 0 && (
                            <CustomPagination recordsPerPage={REVIEW_ITEMS_FETCH_LIMIT} totalRecords={reviewItemsTotal} currentPage={reviewItemsPage} handlePagination={handleReviewItemsPagination} />
                        )}
                    </>
                ) : (
                    <>
                        {isActivitiesLoading ? (
                            <ul className="w-full">
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((skeletonRow) => (
                                    <li
                                        key={skeletonRow}
                                        className="flex items-start justify-between gap-x-4 px-3 py-3 relative after:absolute after:left-3 after:right-3 after:bottom-0 after:h-px after:bg-utilityGray200 last:after:hidden"
                                    >
                                        <div className="flex items-start gap-x-2 flex-1 min-w-0">
                                            <span className="mt-[5px] h-1.5 w-1.5 rounded-full flex-none custom-loading" />
                                            <div className="flex flex-col min-w-0">
                                                <h6 className="text-xs font-semibold leading-[18px] custom-loading">Status Updated by Lorem Ipsum</h6>
                                                <p className="text-xs mt-0.5 leading-[18px] custom-loading">Lorem Ipsum updated the status of order #WAL-TC3B-0000.</p>
                                            </div>
                                        </div>
                                        <span className="text-xs italic leading-[18px] custom-loading flex-none whitespace-nowrap">10 mins ago</span>
                                    </li>
                                ))}
                            </ul>
                        ) : activityDisplayItems.length === 0 ? (
                            <NotFoundUI
                                title={t('Dashboard.LatestUpdates.No_Updates_Title')}
                                desc={t('Dashboard.LatestUpdates.No_Updates_Desc')}
                                containerClassName="!h-auto !my-0"
                                iconClassName="!hidden"
                                isFixedHeight={true}
                                isEmptyBackground
                                iconSecondaryClassName="!hidden"
                            />
                        ) : (
                            <ul className="w-full">
                                {activityDisplayItems.map((item) => (
                                    <li
                                        key={item.id}
                                        className={`flex items-start justify-between gap-x-4 px-3 py-3 relative after:absolute after:left-3 after:right-3 after:bottom-0 after:h-px after:bg-utilityGray200 last:after:hidden ${
                                            item.entityId ? 'cursor-pointer hover:bg-utilityGray100 transition-colors' : ''
                                        }`}
                                        onClick={item.entityId ? () => handleActivityRowClick(item) : undefined}
                                    >
                                        <div className="flex items-start gap-x-2 flex-1 min-w-0">
                                            <span className={`mt-[5px] h-1.5 w-1.5 rounded-full flex-none ${item.dotColor}`} />
                                            <div className="flex flex-col min-w-0">
                                                <h6 className="text-xs font-semibold text-textSecondary truncate leading-[18px]">{item.titleWithActor}</h6>
                                                <ActivityMessage item={item} />
                                            </div>
                                        </div>
                                        <span className="text-xs italic font-normal text-grayLight600 leading-[18px] whitespace-nowrap flex-none">{item.timeLabel}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {activitiesTotal > 0 && (
                            <CustomPagination recordsPerPage={ACTIVITY_FETCH_LIMIT} totalRecords={activitiesTotal} currentPage={activitiesPage} handlePagination={handleActivitiesPagination} />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ActivityFeed;
